<template>
  <v-container>
    <v-row class="mt-13" v-if="!store.isPrenatal">
      <h2 class="mb-6">
        Select one or more test categories
      </h2>
    </v-row>
    <v-row v-if="!store.isPrenatal">
      <div v-for="categoryObj in postnatalCategories" :key="categoryObj.id">
        <!-- When hovering over postnatal categories with label property, tooltip is displayed -->
        <v-tooltip v-if="categoryObj.label" bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                :id="`category-${categoryObj.id}`"
                :key="categoryObj.id"
                depressed
                :class="getButtonColor(categoryObj)"
                @click="toggleCategory(categoryObj.name)"
                style="padding: 30px 25px; font-size: 16px; margin-right: 15px; margin-bottom: 15px;"
              >
                {{ categoryObj.name }}
              </v-btn>
            </span>
          </template>
          <span>{{ categoryObj.label }}</span>
        </v-tooltip>
        <v-btn
          v-else
          :id="`category-${categoryObj.id}`"
          :key="categoryObj.id"
          depressed
          :class="getButtonColor(categoryObj)"
          @click="toggleCategory(categoryObj.name)"
          style="padding: 30px 25px; font-size: 16px; margin-right: 15px; margin-bottom: 15px;"
          >{{ categoryObj.name }}
        </v-btn>
      </div>
    </v-row>

    <!-- PRENATAL TESTS -->
    <DisplayTests
      v-if="store.isPrenatal"
      topic="prenatal"
      heading="Prenatal tests"
      :tests="
        Object.values(store.showTests).filter((test) =>
          test.category.includes('prenatal')
        )
      "
      :selectedTests="selectedTests"
      :isValidForInteraction="isValidForInteraction"
      :getSampleSupportLabel="getSampleSupportLabel"
    />

    <!-- POSTNATAL TESTS -->
    <div v-if="!store.isPrenatal">
      <div v-for="category in selectedPostnatalCategories" :key="category">
        <DisplayTests
          v-if="category === 'Frequently requested'"
          topic="freqReq"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter((test) =>
              test.category.includes('freqReq')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
        <DisplayTests
          v-if="category === 'Medicare testing'"
          topic="medicare"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter((test) =>
              test.category.includes('medicare')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
        <DisplayTests
          v-if="category === 'Exome/Genome Sequencing'"
          topic="genomics"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter((test) =>
              test.category.includes('genomics')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
        <DisplayTests
          v-if="category === 'Kidney disease - Medicare testing'"
          topic="kidney"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter((test) =>
              test.category.includes('kidney')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
        <DisplayTests
          v-if="category === 'Mitochondrial'"
          topic="mito"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter((test) =>
              test.category.includes('mito')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
        <DisplayTests
          v-if="category === 'Deafness'"
          topic="deafness"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter((test) =>
              test.category.includes('deafness')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
        <DisplayTests
          v-if="category === 'Neurogenetics'"
          topic="neurogen"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter((test) =>
              test.category.includes('neurogen')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
        <DisplayTests
          v-if="category === 'Cytogenetics'"
          topic="cytogenetics"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter((test) =>
              test.category.includes('cytogenetics')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
        <DisplayTests
          v-if="category === 'All tests'"
          topic="all"
          :heading="category"
          :tests="
            Object.values(store.showTests).filter(
              (test) => !test.category.includes('prenatal')
            )
          "
          :selectedTests="selectedTests"
          :isValidForInteraction="isValidForInteraction"
          :getSampleSupportLabel="getSampleSupportLabel"
        />
      </div>
    </div>
  </v-container>
</template>

<script>
import DisplayTests from "./DisplayTests.vue";
import { useSectionStore } from "../store/SectionStore";
import { EventBus } from "../event-bus";

const PRENATAL_CATEGORY = [{ id: "prenatal", name: "Prenatal" }];

const POSTNATAL_CATEGORIES = [
  { id: "frequent", name: "Frequently requested" },
  {
    id: "medicare",
    name: "Medicare testing",
    alternativeBg: "green-category-btn",
  },
  {
    id: "genomics",
    name: "Exome/Genome Sequencing",
    label: "Panel testing ordered via exome or whole genome sequencing",
  },
  { id: "kidney", name: "Kidney disease - Medicare testing" },
  { id: "mito", name: "Mitochondrial" },
  { id: "deafness", name: "Deafness" },
  { id: "neurogen", name: "Neurogenetics" },
  { id: "cytogenetics", name: "Cytogenetics" },
  { id: "all", name: "All tests" },
];

export default {
  props: {
    selectedTests: Array,
    isValidForInteraction: Function,
    getSampleSupportLabel: Function,
  },
  components: { DisplayTests },
  data: function() {
    return {
      selectedPostnatalCategories: [],
      postnatalCategories: POSTNATAL_CATEGORIES,
      prenatalCategory: PRENATAL_CATEGORY,
    };
  },
  setup() {
    const store = useSectionStore();
    return {
      store,
    };
  },
  mounted() {
    EventBus.$on("clearSelectedCategories", () => {
      this.selectedPostCategories = [];
    });
    if (!useSectionStore().isPrenatal) {
      this.selectedPostnatalCategories = ["Frequently requested"];
    }
  },
  methods: {
    toggleCategory(categoryName) {
      if (this.selectedPostnatalCategories.includes(categoryName)) {
        this.selectedPostnatalCategories = this.selectedPostnatalCategories.filter(
          (selectedCategory) => selectedCategory !== categoryName
        );
      } else {
        this.selectedPostnatalCategories.push(categoryName);
      }
    },
    /** If a test category is selected, the button's background color is always dark blue.
     * If the test category is unselected, the button's background color depends on if the category
     * has `alternativeBg` property
     */
    getButtonColor(categoryObj) {
      if (this.selectedPostnatalCategories.includes(categoryObj.name)) {
        return "selected-category-btn";
      }

      return categoryObj.alternativeBg
        ? categoryObj.alternativeBg
        : "category-btn";
    },
  },
};
</script>

<style scoped>
.category-btn {
  border: 1px solid #00538b;
  background: #fff !important;
}

.green-category-btn {
  border: 1px solid #00538b;
  background: #5ebd6d !important;
}

.selected-category-btn {
  background: #00538b !important;
  color: #fff !important;
}
</style>
